import { Injectable, OnDestroy } from '@angular/core';
import { SphereNotification } from './+state/notifications/notification.reducer';
import {
    IWebSocketEventEntityType,
    IWebSocketEventOperation,
    WebSocketEvent,
    WebSocketEventEntityType,
    WebSocketEventOperation
} from './ws-event.service';
import { SphereWSEvent } from './+state/events/events.reducer';
import { GlobalNotificationHandlerDefinition, SphereEventHandler, SphereNotificationBuilder } from './global-handlers/global-handler';
import { notificationAllHandler } from './global-handlers/sphere-notification.handlers';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SphereNotificationHandlerConfig {}
export type SphereNotificationHandlerRegistryEntry = {
    isCustomNotification: boolean;
    correlationOnly: boolean;
    notificationBuilder?: SphereNotificationBuilder<any>;
    handlers?: SphereEventHandler[];
};

@Injectable({
    providedIn: 'root'
})
export class EventHandlersService implements OnDestroy {
    private registry = new Map<string, SphereNotificationHandlerRegistryEntry>();
    private sphereNotificationSystemHandler: GlobalNotificationHandlerDefinition<WebSocketEvent> = notificationAllHandler;

    constructor() {
        /**
         * Sphere notifications
         */

        this.registerHandler(
            notificationAllHandler.entity,
            notificationAllHandler.operation,
            notificationAllHandler.isCustomNotification,
            notificationAllHandler.correlationOnly,
            notificationAllHandler.notificationBuilder
        );
    }

    ngOnDestroy() {
        this.registry.clear();
    }

    registerHandler<D = any>(
        entity: WebSocketEventEntityType | string,
        operations: WebSocketEventOperation[] | WebSocketEventOperation | string[] | string,
        isCustomNotification = false,
        correlationOnly = false,
        notificationBuilder?: SphereNotificationBuilder<D>,
        callback?: SphereEventHandler
    ) {
        if (!Array.isArray(operations)) {
            operations = [operations];
        }

        for (const operation of operations) {
            const entityOperation = `${entity}-${operation}`;
            if (this.registry.has(entityOperation)) {
                const current = this.registry.get(entityOperation);
                this.registry.set(entityOperation, {
                    ...current,
                    handlers: [...(current?.handlers || []), callback]
                } as SphereNotificationHandlerRegistryEntry);
            } else {
                this.registry.set(entityOperation, {
                    isCustomNotification: isCustomNotification,
                    correlationOnly,
                    notificationBuilder,
                    handlers: callback ? [callback] : []
                });
            }
        }
    }

    removeHandler(entity: WebSocketEventEntityType | string, operation: WebSocketEventOperation | string) {
        const event = `${entity}-${operation}`;
        if (this.registry.has(event)) {
            this.registry.delete(event);
        }
    }

    handle(entity: WebSocketEventEntityType | string, operation: WebSocketEventOperation | string, notification: SphereNotification) {
        const event = `${entity}-${operation}`;
        if (this.registry.has(event)) {
            for (const handler of this.registry.get(event)?.handlers || []) {
                handler(notification);
            }
        }
    }

    createNotificationHandler(
        entity: IWebSocketEventEntityType,
        operation: IWebSocketEventOperation,
        config?: SphereNotificationHandlerConfig
    ): SphereEventHandler {
        return notification => {
            // Custom notification generator.
        };
    }

    isCustomNotificationEvent(entity: IWebSocketEventEntityType, operation: IWebSocketEventOperation): boolean {
        const event = `${entity}-${operation}`,
            allEvent = `${entity}-${WebSocketEventOperation.all}`;
        if (this.registry.has(event)) {
            return this.registry.get(event)?.isCustomNotification || false;
        } else if (this.registry.has(allEvent)) {
            return this.registry.get(allEvent)?.isCustomNotification || false;
        }
        return false;
    }

    isSphereNotificationEvent(event: SphereWSEvent): boolean {
        return event.notification || false;
    }

    isCorrelationOnlyEvent(entity: IWebSocketEventEntityType, operation: IWebSocketEventOperation): boolean {
        const event = `${entity}-${operation}`,
            allEvent = `${entity}-${WebSocketEventOperation.all}`;
        if (this.registry.has(event)) {
            return this.registry.get(event)?.correlationOnly || false;
        } else if (this.registry.has(allEvent)) {
            return this.registry.get(allEvent)?.correlationOnly || false;
        }
        return false;
    }

    public buildNotification(event: SphereWSEvent) {
        if (event.notification) {
            return this.buildSphereNotification(event);
        }
        return this.buildCustomNotification(event);
    }

    private buildCustomNotification(event: SphereWSEvent) {
        const eventType = `${event['entity-type']}-${event.operation}`,
            allEventType = `${event['entity-type']}-${WebSocketEventOperation.all}`;

        if (this.registry.has(eventType) && !!this.registry.get(eventType)?.notificationBuilder) {
            return this.build(eventType, event);
        } else if (this.registry.has(allEventType) && !!this.registry.get(allEventType)?.notificationBuilder) {
            return this.build(allEventType, event);
        }
        return undefined;
    }

    private buildSphereNotification(event: SphereWSEvent) {
        if (this.sphereNotificationSystemHandler.notificationBuilder) {
            return this.sphereNotificationSystemHandler.notificationBuilder(event);
        }
        return null;
    }

    private build(eventName: string, event: SphereWSEvent) {
        const handler = this.registry.get(eventName);
        if (handler && handler.notificationBuilder) {
            return handler.notificationBuilder(event);
        }
        return null;
    }
}
